/**
 * リッチテキスト本文の共通タイポグラフィ
 *
 * 目的: 記事本文（TipTap の出力）の見た目を1か所で決める。
 *
 * 経緯: 同じクラス一覧が公開ページ（HtmlRenderer / MemberPostHtmlRenderer）と管理画面の
 * プレビュー（HtmlRendererClient）に分かれて置かれており、片方だけ直されて
 * 「管理画面で見た仕上がりと公開ページが違う」事故が起きた（本文写真の幅）。
 * 使う側はサニタイズ関数だけが違い、見た目はここを共有する。
 *
 * React に依存しないモジュールに置いてあるのは、'use client' の有無を問わず
 * どの描画側からも読めるようにするため。
 */
const RICH_TEXT_CLASSES = [
  // 既存デザインに寄せる最低限のタイポグラフィ
  'text-[15px] leading-7 text-gray-700/90',
  // リッチテキスト内の要素間隔
  '[&_p]:mt-3',
  '[&_h1]:mt-8 [&_h1]:font-serif [&_h1]:text-2xl [&_h1]:text-gray-800 md:[&_h1]:text-3xl',
  '[&_h2]:mt-8 [&_h2]:font-serif [&_h2]:text-xl [&_h2]:text-gray-800',
  '[&_h3]:mt-6 [&_h3]:font-serif [&_h3]:text-lg [&_h3]:text-gray-800',
  '[&_ul]:my-4 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5',
  '[&_ol]:my-4 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5',
  '[&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-emerald-200 [&_blockquote]:bg-emerald-50/40 [&_blockquote]:px-4 [&_blockquote]:py-3',
  '[&_a]:text-emerald-700 [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-emerald-800',
  // 写真の幅は書いた人が決める（本文HTMLの width 属性）。ここで w-full を当てると
  // CSS が属性に勝ってしまい、何を指定しても本文幅いっぱいに引き伸ばされる。
  // max-w-full と h-auto は「狭い画面では縮める・そのとき潰さない」ための保険
  '[&_img]:my-6 [&_img]:block [&_img]:mx-auto [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-sm [&_img]:border [&_img]:border-gray-100 [&_img]:bg-gray-50',
  // 幅を指定していない写真（この機能より前に書かれた記事）は、そのままだと本文幅まで
  // 引き伸ばされた縦長写真が縦に伸び続ける。高さを画面の7割で止めて塔になるのを防ぐ
  // （編集画面側の ResizableBodyImage の UNSIZED_MAX_HEIGHT と同じ値にしてある）
  '[&_img:not([width])]:max-h-[70vh] [&_img:not([width])]:w-auto',
  '[&_hr]:my-8 [&_hr]:border-gray-200',
  '[&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.95em] [&_code]:text-gray-800',
];

/** 本文の描画に使う class 文字列。呼び出し側の追加クラスは末尾に足す */
export function richTextClassName(className?: string): string {
  return [...RICH_TEXT_CLASSES, className ?? ''].join(' ');
}
